import React, { createContext, useContext, useEffect, useReducer, useRef } from 'react';

/**
 * Builder context manages experiment draft state and UI/navigation state.
 * This is MVP-scoped: experiment -> blocks -> trials.
 * Persistence: localStorage (debounced) keyed by draftId.
 */

const BuilderContext = createContext(null);

// Stable key ordering serializer
const canonicalStringify = (obj) => {
  const seen = new WeakSet();
  const order = (value) => {
    if (value && typeof value === 'object') {
      if (seen.has(value)) return '"[Circular]"';
      seen.add(value);
      if (Array.isArray(value)) {
        return '[' + value.map(v => order(v)).join(',') + ']';
      }
      return '{' + Object.keys(value).sort().map(k => JSON.stringify(k) + ':' + order(value[k])).join(',') + '}';
    }
    // Normalize undefined → "undefined" so resulting string is valid JSON
    if (value === undefined) return '"undefined"';
    return JSON.stringify(value);
  };
  return order(obj);
};

const defaultDraft = () => ({
  id: null,
  meta: { title: 'Untitled Experiment', introduction: '', debrief: '' },
  blocks: [], // {id,type,title, trials|questions|stimuli:[]}
  variants: [], // working full-clone variants: [{ id,label, blocks:[...], agents:[{id}], createdAt }]
  assignment: { strategy: 'round_robin' } // initial default strategy
});

// UI/navigation state shape
const defaultUI = {
  level: 'experiment', // 'experiment' | 'block' | 'trial' | 'variant'
  selectedVariantId: null,
  selectedBlockId: null,
  selectedTrialId: null,
  orientation: 'vertical', // or 'horizontal'
  validationOpen: false
};

const initialState = (draft) => ({
  draft: draft || defaultDraft(),
  ui: { ...defaultUI },
  validation: { errors: [], warnings: [] },
  dirty: false,
});

function reducer(state, action) {
  switch (action.type) {
    case 'LOAD_DRAFT':
      return { ...state, draft: action.draft, dirty: false, ui: { ...defaultUI } };
    case 'SET_META':
      return { ...state, draft: { ...state.draft, meta: { ...state.draft.meta, ...action.meta } }, dirty: true };
    case 'ADD_BLOCK': {
      if (state.draft.variants && state.draft.variants.length) {
        console.warn('[Builder] Base frozen – cannot add block after variants created');
        return state;
      }
      const id = action.id;
      const base = { id, type: action.blockType, title: action.title || `${action.blockType} block`, introText: '', outroText: '', randomization: 'sequential' };
      let block;
      if (action.blockType === 'scale') {
        block = { ...base, scaleId: null, scaleMeta: null, questions: [], trials: [], stimuli: [] };
      } else if (action.blockType === 'questionnaire') {
        block = { ...base, questions: [], trials: [], stimuli: [] };
      } else if (action.blockType === 'stimuli') {
        // Add future-facing fields for stimuli set selection
        block = { ...base, stimuliSetId: null, stimuliSetMeta: null, stimuli: [], trials: [], questions: [] };
      } else if (action.blockType === 'primed_conversation') {
        block = { ...base,
          layout: { orientation: 'horizontal', leftPane: 'stimuli', rightPane: 'conversation' },
          accessMode: 'embedded',
          conversationMode: 'single_for_block', // or 'per_stimulus'
          interpreter: { strategy: 'auto', template: '' },
          conversation: { trials: [ { id: crypto.randomUUID(), engine: { provider:'', model:'', temperature:0.7, top_p:1 }, agents: [], limit:{ type:'messages', value:10 }, task_description:'', expected_output:'' } ] },
          stimuliSetId: null, stimuliSetMeta: null,
          stimuli: [],
          questions: [], // reserved (not used directly), per stimulus questions in stimuli[]
          trials: [] // not used but kept for structural parity
        };
      } else if (action.blockType === 'conversation') {
        block = { ...base, trials: [], questions: [], stimuli: [] };
      } else {
        block = { ...base, trials: [], questions: [], stimuli: [] };
      }
      return { ...state, draft: { ...state.draft, blocks: [...state.draft.blocks, block] }, dirty: true };
    }
    case 'UPDATE_BLOCK': {
      if (state.draft.variants && state.draft.variants.length) {
        console.warn('[Builder] Base frozen – cannot update block after variants created');
        return state;
      }
      const blocks = state.draft.blocks.map(b => b.id === action.id ? { ...b, ...action.patch } : b);
      return { ...state, draft: { ...state.draft, blocks }, dirty: true };
    }
    case 'REMOVE_BLOCK': {
      if (state.draft.variants && state.draft.variants.length) {
        console.warn('[Builder] Base frozen – cannot remove block after variants created');
        return state;
      }
      const blocks = state.draft.blocks.filter(b => b.id !== action.id);
      return { ...state, draft: { ...state.draft, blocks }, dirty: true, ui: { ...state.ui, level: 'experiment', selectedBlockId: null, selectedTrialId: null } };
    }
    case 'REORDER_BLOCKS': {
      if (state.draft.variants && state.draft.variants.length) {
        console.warn('[Builder] Base frozen – cannot reorder blocks after variants created');
        return state;
      }
      return { ...state, draft: { ...state.draft, blocks: action.blocks }, dirty: true };
    }
    case 'ADD_TRIAL': {
      if (state.draft.variants && state.draft.variants.length) {
        console.warn('[Builder] Base frozen – cannot add trial after variants created');
        return state;
      }
      const blocks = state.draft.blocks.map(b => {
        if (b.id !== action.blockId) return b;
        if (b.type === 'conversation') {
          return { ...b, trials: [...(b.trials||[]), { id: action.trialId, agentRef: '', notes: '', engine: { provider: '', model: '', temperature: 0.7, top_p: 1 }, agents: [], limit: { type: 'messages', value: 10 }, task_description:'', expected_output:'' }] };
        }
        if (b.type === 'questionnaire' || b.type === 'scale') {
          return { ...b, questions: [...(b.questions||[]), { id: action.trialId, type: 'likert', text: '', options: ['1','2','3','4','5','6','7'], required: true }] };
        }
        if (b.type === 'stimuli') {
          return { ...b, stimuli: [...(b.stimuli||[]), { id: action.trialId, label: 'Stimulus', media: null, mediaMeta: null, exposure: 0, size: { width: 0, height: 0 }, position: { x: 0, y: 0 }, questions: [] }] };
        }
        if (b.type === 'primed_conversation') {
         // Adding a stimulus item (trial) with sequential Trial N naming and optional per-stimulus conversation scaffold
         const nextIndex = (b.stimuli?.length || 0) + 1;
         const label = `Trial ${nextIndex}`;
         return { ...b, stimuli: [...(b.stimuli||[]), { id: action.trialId, label, media: null, mediaMeta: null, exposure:0, size:{ width:0, height:0 }, position:{ x:0, y:0 }, questions: [], conversation: (b.conversationMode === 'per_stimulus' ? { engine:{ provider:'', model:'', temperature:0.7, top_p:1 }, agents:[], limit:{ type:'messages', value:10 }, task_description:'', expected_output:'' } : undefined) }] };
        }
        return b;
      });
      return { ...state, draft: { ...state.draft, blocks }, dirty: true };
    }
    case 'UPDATE_TRIAL': {
      if (state.draft.variants && state.draft.variants.length) {
        console.warn('[Builder] Base frozen – cannot update trial after variants created');
        return state;
      }
      const { blockId, trialId, patch } = action;
      const blocks = state.draft.blocks.map(b => {
        if (b.id !== blockId) return b;
        if (b.type === 'conversation') {
          return { ...b, trials: b.trials.map(t => t.id === trialId ? { ...t, ...patch } : t) };
        }
        if (b.type === 'questionnaire' || b.type === 'scale') {
          return { ...b, questions: b.questions.map(q => q.id === trialId ? { ...q, ...patch } : q) };
        }
        if (b.type === 'stimuli') {
          return { ...b, stimuli: b.stimuli.map(s => s.id === trialId ? { ...s, ...patch } : s) };
        }
        if (b.type === 'primed_conversation') {
          return { ...b, stimuli: b.stimuli.map(s => {
            if (s.id !== trialId) return s;
            const next = { ...s, ...patch };
            // Keep agents in sync for per-stimulus mode when UI edits root-level agents
            if (b.conversationMode === 'per_stimulus' && Array.isArray(patch?.agents)) {
              if (!next.conversation || typeof next.conversation !== 'object') {
                next.conversation = { engine:{ provider:'', model:'', temperature:0.7, top_p:1 }, agents:[], limit:{ type:'messages', value:10 }, task_description:'', expected_output:'' };
              }
              next.conversation = { ...next.conversation, agents: [...patch.agents] };
            }
            return next;
          }) };
        }
        return b;
      });
      return { ...state, draft: { ...state.draft, blocks }, dirty: true };
    }
    case 'REMOVE_TRIAL': {
      if (state.draft.variants && state.draft.variants.length) {
        console.warn('[Builder] Base frozen – cannot remove trial after variants created');
        return state;
      }
      const { blockId, trialId } = action;
      const blocks = state.draft.blocks.map(b => {
        if (b.id !== blockId) return b;
        if (b.type === 'conversation') return { ...b, trials: b.trials.filter(t => t.id !== trialId) };
        if (b.type === 'questionnaire' || b.type === 'scale') return { ...b, questions: b.questions.filter(q => q.id !== trialId) };
        if (b.type === 'stimuli') return { ...b, stimuli: b.stimuli.filter(s => s.id !== trialId) };
        if (b.type === 'primed_conversation') return { ...b, stimuli: b.stimuli.filter(s => s.id !== trialId) };
        return b;
      });
      return { ...state, draft: { ...state.draft, blocks }, dirty: true };
    }
    case 'REORDER_BLOCK_CHILDREN': {
      if (state.draft.variants && state.draft.variants.length) {
        console.warn('[Builder] Base frozen – cannot reorder trials after variants created');
        return state;
      }
      const { blockId, items } = action; // items already ordered list of entities with id
      const blocks = state.draft.blocks.map(b => {
        if (b.id !== blockId) return b;
        if (b.type === 'conversation') return { ...b, trials: items };
        if (b.type === 'questionnaire' || b.type === 'scale') return { ...b, questions: items };
        if (b.type === 'stimuli') return { ...b, stimuli: items };
        if (b.type === 'primed_conversation') return { ...b, stimuli: items };
        return b;
      });
      return { ...state, draft: { ...state.draft, blocks }, dirty: true };
    }
    case 'NAVIGATE':
      return { ...state, ui: { ...state.ui, ...action.uiPatch } };
    case 'SET_VARIANTS': {
      return { ...state, draft: { ...state.draft, variants: action.variants }, dirty: true };
    }
    case 'ADD_VARIANT': {
      const nextLetter = String.fromCharCode(65 + state.draft.variants.length); // A,B,C...
      const variantId = action.id;
      const blocksClone = JSON.parse(JSON.stringify(state.draft.blocks));
      // Collect agents referenced in base blocks
      const agentSet = new Set();
      blocksClone.forEach(b => {
        if (b.type === 'conversation') (b.trials||[]).forEach(t => (t.agents||[]).forEach(a=>agentSet.add(a)));
        if (b.type === 'primed_conversation') {
          if (b.conversationMode === 'single_for_block') (b.conversation?.trials||[]).forEach(ct => (ct.agents||[]).forEach(a=>agentSet.add(a)));
          else (b.stimuli||[]).forEach(s => (s.conversation?.agents||[]).forEach(a=>agentSet.add(a)));
        }
      });
      const variant = { id: variantId, label: `Variant ${nextLetter}`, blocks: blocksClone, agents: Array.from(agentSet).map(id=>({ id })), createdAt: new Date().toISOString() };
      return { ...state, draft: { ...state.draft, variants: [...state.draft.variants, variant] }, dirty: true };
    }
    case 'SET_ASSIGNMENT': {
      return { ...state, draft: { ...state.draft, assignment: { ...state.draft.assignment, ...action.assignment } }, dirty: true };
    }
    // -------- Variant editing actions --------
    case 'ADD_VARIANT_BLOCK': {
      const { variantId, blockType, id } = action;
      const vIndex = state.draft.variants.findIndex(v => v.id === variantId);
      if (vIndex === -1) return state;
      const variant = state.draft.variants[vIndex];
      const base = { id, type: blockType, title: `${blockType} block`, introText:'', outroText:'', randomization:'sequential' };
      let block;
  if (blockType === 'scale') block = { ...base, scaleId:null, scaleMeta:null, questions:[], trials:[], stimuli:[] };
      else if (blockType === 'questionnaire') block = { ...base, questions:[], trials:[], stimuli:[] };
      else if (blockType === 'stimuli') block = { ...base, stimuliSetId:null, stimuliSetMeta:null, stimuli:[], trials:[], questions:[] };
  else if (blockType === 'primed_conversation') block = { ...base, layout:{ orientation:'horizontal', leftPane:'stimuli', rightPane:'conversation' }, accessMode:'embedded', conversationMode:'single_for_block', interpreter:{ strategy:'auto', template:'' }, conversation:{ trials:[ { id: crypto.randomUUID(), engine:{ provider:'', model:'', temperature:0.7, top_p:1 }, agents:[], limit:{ type:'messages', value:10 }, task_description:'', expected_output:'' } ] }, stimuliSetId:null, stimuliSetMeta:null, stimuli:[], questions:[], trials:[] };
  else block = { ...base, trials:[], questions:[], stimuli:[] };
      const newVariants = [...state.draft.variants];
      newVariants[vIndex] = { ...variant, blocks:[...variant.blocks, block] };
      return { ...state, draft: { ...state.draft, variants: newVariants }, dirty:true };
    }
    case 'UPDATE_VARIANT_BLOCK': {
      const { variantId, blockId, patch } = action;
      const vIndex = state.draft.variants.findIndex(v => v.id === variantId);
      if (vIndex === -1) return state;
      const variant = state.draft.variants[vIndex];
      const blocks = variant.blocks.map(b => b.id === blockId ? { ...b, ...patch } : b);
      const newVariants = [...state.draft.variants];
      newVariants[vIndex] = { ...variant, blocks };
      return { ...state, draft: { ...state.draft, variants: newVariants }, dirty:true };
    }
    case 'REMOVE_VARIANT_BLOCK': {
      const { variantId, blockId } = action;
      const vIndex = state.draft.variants.findIndex(v => v.id === variantId);
      if (vIndex === -1) return state;
      const variant = state.draft.variants[vIndex];
      const blocks = variant.blocks.filter(b => b.id !== blockId);
      const newVariants = [...state.draft.variants];
      newVariants[vIndex] = { ...variant, blocks };
      return { ...state, draft: { ...state.draft, variants: newVariants }, dirty:true };
    }
    case 'REORDER_VARIANT_BLOCKS': {
      const { variantId, blocks } = action;
      const vIndex = state.draft.variants.findIndex(v => v.id === variantId);
      if (vIndex === -1) return state;
      const variant = state.draft.variants[vIndex];
      const newVariants = [...state.draft.variants];
      newVariants[vIndex] = { ...variant, blocks };
      return { ...state, draft: { ...state.draft, variants: newVariants }, dirty:true };
    }
    case 'ADD_VARIANT_TRIAL': {
      const { variantId, blockId, trialId } = action;
      const vIndex = state.draft.variants.findIndex(v => v.id === variantId);
      if (vIndex === -1) return state;
      const variant = state.draft.variants[vIndex];
      const blocks = variant.blocks.map(b => {
        if (b.id !== blockId) return b;
        if (b.type === 'conversation') return { ...b, trials:[...(b.trials||[]), { id: trialId, agentRef:'', notes:'', engine:{ provider:'', model:'', temperature:0.7, top_p:1 }, agents:[], limit:{ type:'messages', value:10 }, task_description:'', expected_output:'' }] };
        if (b.type === 'questionnaire' || b.type === 'scale') return { ...b, questions:[...(b.questions||[]), { id: trialId, type:'likert', text:'', options:['1','2','3','4','5','6','7'], required:true }] };
        if (b.type === 'stimuli') return { ...b, stimuli:[...(b.stimuli||[]), { id: trialId, label:'Stimulus', media:null, mediaMeta:null, exposure:0, size:{ width:0, height:0 }, position:{ x:0, y:0 }, questions:[] }] };
        if (b.type === 'primed_conversation') {
          const nextIndex = (b.stimuli?.length||0)+1;
          const label = `Trial ${nextIndex}`;
          return { ...b, stimuli:[...(b.stimuli||[]), { id: trialId, label, media:null, mediaMeta:null, exposure:0, size:{ width:0, height:0 }, position:{ x:0, y:0 }, questions:[], conversation:(b.conversationMode==='per_stimulus'? { engine:{ provider:'', model:'', temperature:0.7, top_p:1 }, agents:[], limit:{ type:'messages', value:10 }, task_description:'', expected_output:'' }: undefined) }] };
        }
        return b;
      });
      const newVariants = [...state.draft.variants];
      newVariants[vIndex] = { ...variant, blocks };
      return { ...state, draft: { ...state.draft, variants: newVariants }, dirty:true };
    }
    case 'UPDATE_VARIANT_TRIAL': {
      const { variantId, blockId, trialId, patch } = action;
      const vIndex = state.draft.variants.findIndex(v => v.id === variantId);
      if (vIndex === -1) return state;
      const variant = state.draft.variants[vIndex];
      const blocks = variant.blocks.map(b => {
        if (b.id !== blockId) return b;
        if (b.type === 'conversation') return { ...b, trials: b.trials.map(t => t.id === trialId ? { ...t, ...patch } : t) };
        if (b.type === 'questionnaire' || b.type === 'scale') return { ...b, questions: b.questions.map(q => q.id === trialId ? { ...q, ...patch } : q) };
        if (b.type === 'stimuli') return { ...b, stimuli: b.stimuli.map(s => s.id === trialId ? { ...s, ...patch } : s) };
        if (b.type === 'primed_conversation') {
          return { ...b, stimuli: b.stimuli.map(s => {
            if (s.id !== trialId) return s;
            const next = { ...s, ...patch };
            if (b.conversationMode === 'per_stimulus' && Array.isArray(patch?.agents)) {
              if (!next.conversation || typeof next.conversation !== 'object') {
                next.conversation = { engine:{ provider:'', model:'', temperature:0.7, top_p:1 }, agents:[], limit:{ type:'messages', value:10 }, task_description:'', expected_output:'' };
              }
              next.conversation = { ...next.conversation, agents: [...patch.agents] };
            }
            return next;
          }) };
        }
        return b;
      });
      const newVariants = [...state.draft.variants];
      newVariants[vIndex] = { ...variant, blocks };
      return { ...state, draft: { ...state.draft, variants: newVariants }, dirty:true };
    }
    case 'REMOVE_VARIANT_TRIAL': {
      const { variantId, blockId, trialId } = action;
      const vIndex = state.draft.variants.findIndex(v => v.id === variantId);
      if (vIndex === -1) return state;
      const variant = state.draft.variants[vIndex];
      const blocks = variant.blocks.map(b => {
        if (b.id !== blockId) return b;
        if (b.type === 'conversation') return { ...b, trials: b.trials.filter(t => t.id !== trialId) };
        if (b.type === 'questionnaire' || b.type === 'scale') return { ...b, questions: b.questions.filter(q => q.id !== trialId) };
        if (b.type === 'stimuli' || b.type === 'primed_conversation') return { ...b, stimuli: b.stimuli.filter(s => s.id !== trialId) };
        return b;
      });
      const newVariants = [...state.draft.variants];
      newVariants[vIndex] = { ...variant, blocks };
      return { ...state, draft: { ...state.draft, variants: newVariants }, dirty:true };
    }
    case 'REORDER_VARIANT_BLOCK_CHILDREN': {
      const { variantId, blockId, items } = action;
      const vIndex = state.draft.variants.findIndex(v => v.id === variantId);
      if (vIndex === -1) return state;
      const variant = state.draft.variants[vIndex];
      const blocks = variant.blocks.map(b => {
        if (b.id !== blockId) return b;
        if (b.type === 'conversation') return { ...b, trials: items };
        if (b.type === 'questionnaire' || b.type === 'scale') return { ...b, questions: items };
        if (b.type === 'stimuli' || b.type === 'primed_conversation') return { ...b, stimuli: items };
        return b;
      });
      const newVariants = [...state.draft.variants];
      newVariants[vIndex] = { ...variant, blocks };
      return { ...state, draft: { ...state.draft, variants: newVariants }, dirty:true };
    }
    case 'SET_ORIENTATION':
      return { ...state, ui: { ...state.ui, orientation: action.orientation } };
    case 'SET_VALIDATION':
      return { ...state, validation: action.validation };
    case 'MARK_SAVED':
      return { ...state, dirty: false };
    default:
      return state;
  }
}

const validateDraft = (draft) => {
  const errors = [];
  const warnings = [];
  if (!draft.meta.title || draft.meta.title.trim() === '') errors.push({ level: 'experiment', message: 'Experiment title is required.' });
  if (draft.blocks.length === 0) errors.push({ level: 'experiment', message: 'At least one block is required.' });
  draft.blocks.forEach((b, idx) => {
    // migration safety: ensure new fields exist if an older draft is loaded
    if (b.randomization === undefined) b.randomization = 'sequential';
    if (b.introText === undefined) b.introText = '';
    if (b.outroText === undefined) b.outroText = '';
    if (!b.title) warnings.push({ level: 'block', blockId: b.id, message: `Block ${idx+1} has no title.` });
    if (b.type === 'conversation' && (!b.trials || b.trials.length === 0)) errors.push({ level: 'block', blockId: b.id, message: 'Conversation block has no trials.' });
    if (b.type === 'questionnaire' && (!b.questions || b.questions.length === 0)) errors.push({ level: 'block', blockId: b.id, message: 'Questionnaire block has no questions.' });
    if (b.type === 'scale') {
      if (!b.scaleId) errors.push({ level: 'block', blockId: b.id, message: 'Scale block has no scale selected.' });
      else if (!b.questions || b.questions.length === 0) warnings.push({ level: 'block', blockId: b.id, message: 'Scale selected but items not populated.' });
    }
    if (b.type === 'stimuli') {
      // migrate older drafts
      if (b.stimuli && b.stimuli.length) {
        b.stimuli = b.stimuli.map(s => ({
          ...s,
          mediaMeta: s.mediaMeta || null,
          exposure: typeof s.exposure === 'number' && !isNaN(s.exposure) ? s.exposure : 0,
          size: s.size || { width: 0, height: 0 },
          position: s.position || { x: 0, y: 0 },
          questions: Array.isArray(s.questions) ? s.questions : []
        }));
        // detailed validation
        b.stimuli.forEach(s => {
          if (s.exposure < 0) errors.push({ level: 'stimulus', blockId: b.id, stimulusId: s.id, message: `Stimulus ${s.label||s.id} has negative exposure.` });
          if (s.size && (s.size.width < 0 || s.size.height < 0)) warnings.push({ level: 'stimulus', blockId: b.id, stimulusId: s.id, message: `Stimulus ${s.label||s.id} size has negative dimension.` });
          if (s.size && (s.size.width === 0 || s.size.height === 0)) warnings.push({ level: 'stimulus', blockId: b.id, stimulusId: s.id, message: `Stimulus ${s.label||s.id} size has zero dimension; may not display.` });
        });
      }
      // If a stimuli set is selected, enforce presence of at least one stimulus
      if (b.stimuliSetId && (!b.stimuli || b.stimuli.length === 0)) errors.push({ level: 'block', blockId: b.id, message: 'Stimuli set selected but no stimuli populated.' });
      if (!b.stimuli || b.stimuli.length === 0) warnings.push({ level: 'block', blockId: b.id, message: 'Stimuli block has no stimuli (will be empty).' });
    }
    if (b.type === 'primed_conversation') {
      // Migrate structure
      if (!b.layout) b.layout = { orientation:'horizontal', leftPane:'stimuli', rightPane:'conversation' };
      if (!b.accessMode) b.accessMode = 'embedded';
      if (!b.conversationMode) b.conversationMode = 'single_for_block';
      if (!b.interpreter) b.interpreter = { strategy:'auto', template:'' };
  if (!b.conversation) b.conversation = { trials:[ { id: crypto.randomUUID(), engine:{ provider:'', model:'', temperature:0.7, top_p:1 }, agents:[], limit:{ type:'messages', value:10 }, task_description:'', expected_output:'' } ] };
      if (!Array.isArray(b.stimuli)) b.stimuli = [];
      // Stimuli migration (reuse logic)
      b.stimuli = b.stimuli.map(s => ({
        ...s,
        mediaMeta: s.mediaMeta || null,
        exposure: typeof s.exposure === 'number' && !isNaN(s.exposure) ? s.exposure : 0,
        size: s.size || { width:0, height:0 },
        position: s.position || { x:0, y:0 },
        questions: Array.isArray(s.questions)? s.questions: [],
        conversation: (b.conversationMode === 'per_stimulus') ? (s.conversation || { engine:{ provider:'', model:'', temperature:0.7, top_p:1 }, agents:[], limit:{ type:'messages', value:10 } }) : undefined
      }));
      // Validation
      const layoutRoles = [b.layout?.leftPane, b.layout?.rightPane];
      if (!(layoutRoles.includes('stimuli') && layoutRoles.includes('conversation'))) errors.push({ level:'block', blockId:b.id, message:'Primed conversation layout must include stimuli and conversation panes.' });
      const convTrials = b.conversation?.trials||[];
      if (b.conversationMode === 'single_for_block') {
        const ct = convTrials[0];
        if (!ct || (ct.agents||[]).length === 0) warnings.push({ level:'block', blockId:b.id, message:'No agents selected for conversation.' });
      } else if (b.conversationMode === 'per_stimulus') {
        b.stimuli.forEach(s=>{
          const agentList = Array.isArray(s?.agents) ? s.agents : (s?.conversation?.agents || []);
          if (!agentList || agentList.length === 0) warnings.push({ level:'stimulus', blockId:b.id, stimulusId:s.id, message:`Stimulus ${s.label||s.id} has no agents.` });
        });
      }
        // Size / dimension warnings similar to stimuli block
        b.stimuli.forEach(s => {
          if (s.size && (s.size.width < 0 || s.size.height < 0)) warnings.push({ level:'stimulus', blockId:b.id, stimulusId:s.id, message:`Stimulus ${s.label||s.id} size has negative dimension.` });
          if (s.size && (s.size.width === 0 || s.size.height === 0)) warnings.push({ level:'stimulus', blockId:b.id, stimulusId:s.id, message:`Stimulus ${s.label||s.id} size has zero dimension; may not display.` });
        });
      if (b.stimuliSetId && b.stimuli.length === 0) errors.push({ level:'block', blockId:b.id, message:'Stimuli set selected but no stimuli populated.' });
    }
    if (!b.randomization) warnings.push({ level: 'block', blockId: b.id, message: 'Block randomization fallback to sequential.' });
  });
  return { errors, warnings };
};

export const BuilderProvider = ({ draftId, children }) => {
  const lsKey = draftId ? `hax_builder_draft_${draftId}` : null;
  const initialDraft = (() => {
    if (lsKey) {
      try { const raw = localStorage.getItem(lsKey); if (raw) return JSON.parse(raw); } catch(e) { /* ignore */ }
    }
    return { ...defaultDraft(), id: draftId || crypto.randomUUID() };
  })();
  const [state, dispatch] = useReducer(reducer, initialDraft, initialState);
  const saveTimer = useRef(null);

  // Autosave
  useEffect(() => {
    if (!lsKey) return;
    if (!state.dirty) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      localStorage.setItem(lsKey, canonicalStringify(state.draft));
      dispatch({ type: 'MARK_SAVED' });
    }, 800);
  }, [state.dirty, state.draft, lsKey]);

  // Re-run validation when draft changes
  useEffect(() => {
    const v = validateDraft(state.draft);
    dispatch({ type: 'SET_VALIDATION', validation: v });
  }, [state.draft]);

  const api = {
    state,
    addBlock: (blockType) => dispatch({ type: 'ADD_BLOCK', blockType, id: crypto.randomUUID() }),
    updateBlock: (id, patch) => dispatch({ type: 'UPDATE_BLOCK', id, patch }),
  setMeta: (meta) => dispatch({ type: 'SET_META', meta }),
    removeBlock: (id) => dispatch({ type: 'REMOVE_BLOCK', id }),
    reorderBlocks: (reordered) => dispatch({ type: 'REORDER_BLOCKS', blocks: reordered }),
    addTrial: (blockId) => dispatch({ type: 'ADD_TRIAL', blockId, trialId: crypto.randomUUID() }),
    updateTrial: (blockId, trialId, patch) => dispatch({ type: 'UPDATE_TRIAL', blockId, trialId, patch }),
    removeTrial: (blockId, trialId) => dispatch({ type: 'REMOVE_TRIAL', blockId, trialId }),
  reorderBlockChildren: (blockId, items) => dispatch({ type: 'REORDER_BLOCK_CHILDREN', blockId, items }),
    navigate: (uiPatch) => dispatch({ type: 'NAVIGATE', uiPatch }),
   addVariant: () => dispatch({ type: 'ADD_VARIANT', id: crypto.randomUUID() }),
  setVariants: (variants) => dispatch({ type:'SET_VARIANTS', variants }),
  addVariantBlock: (variantId, blockType) => dispatch({ type:'ADD_VARIANT_BLOCK', variantId, blockType, id: crypto.randomUUID() }),
  updateVariantBlock: (variantId, blockId, patch) => dispatch({ type:'UPDATE_VARIANT_BLOCK', variantId, blockId, patch }),
  removeVariantBlock: (variantId, blockId) => dispatch({ type:'REMOVE_VARIANT_BLOCK', variantId, blockId }),
  reorderVariantBlocks: (variantId, blocks) => dispatch({ type:'REORDER_VARIANT_BLOCKS', variantId, blocks }),
  addVariantTrial: (variantId, blockId) => dispatch({ type:'ADD_VARIANT_TRIAL', variantId, blockId, trialId: crypto.randomUUID() }),
  updateVariantTrial: (variantId, blockId, trialId, patch) => dispatch({ type:'UPDATE_VARIANT_TRIAL', variantId, blockId, trialId, patch }),
  removeVariantTrial: (variantId, blockId, trialId) => dispatch({ type:'REMOVE_VARIANT_TRIAL', variantId, blockId, trialId }),
  reorderVariantBlockChildren: (variantId, blockId, items) => dispatch({ type:'REORDER_VARIANT_BLOCK_CHILDREN', variantId, blockId, items }),
   setAssignment: (assignment) => dispatch({ type: 'SET_ASSIGNMENT', assignment }),
    setOrientation: (orientation) => dispatch({ type: 'SET_ORIENTATION', orientation }),
    serialize: () => {
      // Deep clone draft then normalize per_stimulus primed_conversation stimuli so agent IDs live under conversation.agents
      const draftClone = JSON.parse(JSON.stringify(state.draft));
      const normalizeStimuliAgents = (blocks) => {
        blocks.forEach(b => {
          if (b && b.type === 'primed_conversation' && b.conversationMode === 'per_stimulus' && Array.isArray(b.stimuli)) {
            b.stimuli.forEach(s => {
              if (!s) return;
              // If UI-maintained root-level agents exist, they are the single source of truth.
              if (Array.isArray(s.agents)) {
                // Ensure conversation object exists
                if (!s.conversation || typeof s.conversation !== 'object') {
                  s.conversation = { engine:{ provider:'', model:'', temperature:0.7, top_p:1 }, agents:[], limit:{ type:'messages', value:10 }, task_description:'', expected_output:'' };
                }
                // Replace (not union) with normalized IDs from s.agents
                const normalized = s.agents.map(a => (typeof a === 'string' ? a : (a && typeof a.id === 'string' ? a.id : null))).filter(Boolean);
                s.conversation.agents = [...normalized];
                delete s.agents; // remove root-level agents to avoid duplication
              } else {
                // No root-level agents: ensure conversation.agents is at least an array
                if (s.conversation && !Array.isArray(s.conversation.agents)) s.conversation.agents = [];
              }
            });
          }
        });
      };
      const collectAgentIdsFromBlocks = (blocks = []) => {
        const ids = new Set();
        blocks.forEach(b => {
          if (!b) return;
          if (b.type === 'conversation') (b.trials||[]).forEach(t => (t.agents||[]).forEach(a => a && ids.add(typeof a === 'string' ? a : a.id)));
          if (b.type === 'primed_conversation') {
            if (b.conversationMode === 'single_for_block') {
              (b.conversation?.trials||[]).forEach(t => (t.agents||[]).forEach(a => a && ids.add(typeof a === 'string' ? a : a.id)));
            } else {
              (b.stimuli||[]).forEach(s => {
                const refs = Array.isArray(s?.conversation?.agents) ? s.conversation.agents : (Array.isArray(s?.agents) ? s.agents : []);
                refs.forEach(a => a && ids.add(typeof a === 'string' ? a : a.id));
              });
            }
          }
        });
        return Array.from(ids).filter(Boolean);
      };
      normalizeStimuliAgents(draftClone.blocks);
      // Also normalize variant blocks if present
      if (Array.isArray(draftClone.variants)) {
        draftClone.variants.forEach(v => {
          if (v && Array.isArray(v.blocks)) {
            normalizeStimuliAgents(v.blocks);
            const ids = collectAgentIdsFromBlocks(v.blocks);
            v.agents = ids.map(id => ({ id }));
          }
        });
      }
      return canonicalStringify(draftClone);
    },
    computeHash: async () => {
      const msg = new TextEncoder().encode(canonicalStringify(state.draft));
      const hashBuffer = await crypto.subtle.digest('SHA-256', msg);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return 'sha256:' + hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    },
    importDraft: (rawDraft) => {
      try {
        const sanitized = sanitizeDraft(rawDraft);
        dispatch({ type: 'LOAD_DRAFT', draft: sanitized });
      } catch (err) {
        console.error('[Builder/importDraft] failed', err);
        throw err;
      }
    }
  };

  return <BuilderContext.Provider value={api}>{children}</BuilderContext.Provider>;
};

export const useBuilder = () => {
  const ctx = useContext(BuilderContext);
  if (!ctx) throw new Error('useBuilder must be used within BuilderProvider');
  return ctx;
};

// -------- Sanitization (placed after exports to avoid capturing stale functions) --------
// Defensive copy & migration for externally provided draft JSON.
function sanitizeDraft(input) {
  if (!input || typeof input !== 'object') throw new Error('Draft must be an object');
  const base = { ...defaultDraft(), ...input };
  if (!base.id) base.id = crypto.randomUUID();
  if (!base.meta || typeof base.meta !== 'object') base.meta = { title: 'Untitled Experiment', introduction: '', debrief: '' };
  base.meta = {
    title: (base.meta.title || '').toString().slice(0, 300) || 'Untitled Experiment',
    introduction: (base.meta.introduction || '').toString(),
    debrief: (base.meta.debrief || '').toString()
  };
  if (!Array.isArray(base.blocks)) base.blocks = [];
  const normalizeId = (id) => {
    if (typeof id === 'string' && id.trim()) return id.trim();
    return crypto.randomUUID();
  };
  base.blocks = base.blocks.map((b, idx) => {
    const type = ['conversation','questionnaire','stimuli','scale','primed_conversation'].includes(b?.type) ? b.type : 'conversation';
    const block = {
      id: normalizeId(b.id || `blk_${idx+1}`),
      type,
      title: (b.title || '').toString().slice(0, 300),
      introText: (b.introText || '').toString(),
      outroText: (b.outroText || '').toString(),
      randomization: ['sequential','shuffle','per-participant-shuffle','reverse'].includes(b.randomization) ? b.randomization : 'sequential'
    };
    if (type === 'conversation') {
      block.trials = Array.isArray(b.trials) ? b.trials.map((t,i)=>({
        id: normalizeId(t.id||`trial_${i+1}`),
        agentRef: (t.agentRef||'').toString(),
        notes: (t.notes||'').toString(),
        engine: sanitizeEngine(t.engine),
        agents: Array.isArray(t.agents)? t.agents.filter(a=> typeof a==='string'):[],
        limit: sanitizeLimit(t.limit)
      })) : [];
      block.questions = [];
      block.stimuli = [];
    } else if (type === 'questionnaire' || type === 'scale') {
      block.questions = Array.isArray(b.questions) ? b.questions.map((q,i)=> sanitizeQuestion(q, i)) : [];
      if (type === 'scale') {
        block.scaleId = b.scaleId || null;
        block.scaleMeta = b.scaleMeta || null;
      }
      block.trials = [];
      block.stimuli = [];
    } else if (type === 'stimuli') {
      block.stimuliSetId = b.stimuliSetId || null;
      block.stimuliSetMeta = b.stimuliSetMeta || null;
      block.stimuli = Array.isArray(b.stimuli)? b.stimuli.map((s,i)=> sanitizeStimulus(s,i)) : [];
      block.trials = [];
      block.questions = [];
    } else if (type === 'primed_conversation') {
      block.layout = sanitizeLayout(b.layout);
      block.accessMode = ['embedded','interpreter','none'].includes(b.accessMode) ? b.accessMode : 'embedded';
      block.conversationMode = ['single_for_block','per_stimulus'].includes(b.conversationMode) ? b.conversationMode : 'single_for_block';
      block.interpreter = sanitizeInterpreter(b.interpreter);
      block.conversation = sanitizeConversationRoot(b.conversation);
      block.stimuliSetId = b.stimuliSetId || null;
      block.stimuliSetMeta = b.stimuliSetMeta || null;
      block.stimuli = Array.isArray(b.stimuli)? b.stimuli.map((s,i)=> sanitizeStimulus(s,i, block.conversationMode === 'per_stimulus')) : [];
      block.trials = []; // not used
      block.questions = [];
    }
    return block;
  });
  // Final validation pass triggers migration side-effects (size etc.)
  validateDraft(base);
  return base;
}

function sanitizeQuestion(q, idx) {
  const id = (typeof q?.id === 'string' && q.id.trim()) ? q.id.trim() : crypto.randomUUID();
  const typeAllowed = ['likert','text','boolean','single_choice','multiple_selection','slider','multiple_choice'];
  let type = typeAllowed.includes(q?.type) ? q.type : 'likert';
  if (type === 'multiple_choice') type = 'single_choice';
  const base = {
    id,
    type,
    text: (q?.text||'').toString(),
    required: q?.required === false ? false : true,
    options: Array.isArray(q?.options) ? q.options.map(o=> String(o)).slice(0, 50) : defaultOptions(type)
  };
  if (type === 'likert') {
    base.likertConfig = sanitizeLikert(q?.likertConfig, base.options);
  } else if (type === 'slider') {
    base.slider = sanitizeSlider(q?.slider);
  } else if (type === 'multiple_selection') {
    base.multipleSelection = sanitizeMultiSel(q?.multipleSelection);
  }
  return base;
}

function defaultOptions(type) {
  if (type === 'likert') return ['1','2','3','4','5','6','7'];
  if (type === 'boolean') return ['Yes','No'];
  if (type === 'single_choice' || type === 'multiple_selection') return ['Option 1','Option 2','Option 3'];
  return [];
}

function sanitizeLikert(cfg, opts) {
  const start = (cfg && (cfg.start === 0 || cfg.start === 1)) ? cfg.start : (opts[0]==='0'?0:1);
  const levels = Math.min(15, Math.max(2, (cfg && typeof cfg.levels === 'number') ? cfg.levels : opts.length));
  // Preserve positional alignment: allow empty entries, but coerce null/undefined to '' for UI safety
  let labels = Array.isArray(cfg?.labels) ? cfg.labels.slice(0, levels) : [];
  labels = labels.map(l => (l === null || l === undefined) ? '' : String(l));
  while (labels.length < levels) labels.push('');
  return { start, levels, labels };
}

function sanitizeSlider(sl) {
  const min = (sl && typeof sl.min === 'number') ? sl.min : 0;
  const max = (sl && typeof sl.max === 'number') ? Math.max(min, sl.max) : Math.max(min, 100);
  let step = (sl && typeof sl.step === 'number' && sl.step > 0) ? sl.step : 1;
  const labels = Array.isArray(sl?.labels) ? sl.labels.filter(l=> typeof l.value==='number' && typeof l.label==='string').slice(0, 100) : [];
  return { min, max, step, labels };
}

function sanitizeMultiSel(ms) {
  if (!ms || typeof ms !== 'object') return { min:0, max:null, layout:'vertical' };
  const layout = ['vertical','horizontal','grid'].includes(ms.layout)? ms.layout : 'vertical';
  const min = (ms.min === null || ms.min === undefined) ? 0 : Math.max(0, parseInt(ms.min,10));
  let max = (ms.max === null || ms.max === undefined) ? null : Math.max(min, parseInt(ms.max,10));
  return { min, max, layout };
}

function sanitizeStimulus(s, idx, withConversation = false) {
  const id = (typeof s?.id === 'string' && s.id.trim()) ? s.id.trim() : crypto.randomUUID();
  const base = {
    id,
    label: (s?.label||`Stimulus ${idx+1}`).toString(),
    media: s?.media || null,
    mediaMeta: s?.mediaMeta || null,
    exposure: (typeof s?.exposure === 'number' && s.exposure >= 0) ? s.exposure : 0,
    size: sanitizeSize(s?.size),
    position: sanitizePosition(s?.position),
    questions: Array.isArray(s?.questions)? s.questions.map((q,i)=> sanitizeQuestion(q,i)) : []
  };
  if (withConversation) {
    base.conversation = sanitizeConversationRoot(s?.conversation, true);
    // Migration: some drafts may have placed agent IDs at root stimulus (s.agents)
    if (Array.isArray(s?.agents) && s.agents.length) {
      const incoming = s.agents.map(a => (typeof a === 'string' ? a : (a && typeof a.id === 'string' ? a.id : null))).filter(Boolean);
      const set = new Set(Array.isArray(base.conversation.agents) ? base.conversation.agents : []);
      incoming.forEach(id => set.add(id));
      base.conversation.agents = Array.from(set);
    }
    // Ensure UI compatibility: root-level agents array for builder editing if conversation.agents present
    if (!Array.isArray(base.agents) && Array.isArray(base.conversation?.agents) && base.conversation.agents.length) {
      base.agents = [...base.conversation.agents];
    } else if (Array.isArray(base.agents)) {
      // If both exist, make sure they stay in sync (favor conversation.agents authoritative)
      const set = new Set(base.conversation.agents || []);
      base.agents.forEach(a=> set.add(a));
      base.agents = Array.from(set);
      base.conversation.agents = [...base.agents];
    }
  }
  return base;
}

function sanitizeSize(sz){
  if (!sz || typeof sz !== 'object') return { width:0, height:0 };
  const width = Math.max(0, parseInt(sz.width||0,10));
  const height = Math.max(0, parseInt(sz.height||0,10));
  return { width, height };
}
function sanitizePosition(p){
  if (!p || typeof p !== 'object') return { x:0, y:0 };
  const x = parseInt(p.x||0,10)||0; const y = parseInt(p.y||0,10)||0; return { x, y };
}

function sanitizeEngine(e){
  if (!e || typeof e !== 'object') return { provider:'', model:'', temperature:0.7, top_p:1 };
  const temperature = typeof e.temperature === 'number' ? e.temperature : 0.7;
  const top_p = typeof e.top_p === 'number' ? e.top_p : 1;
  return { provider:(e.provider||'').toString(), model:(e.model||'').toString(), temperature, top_p };
}
function sanitizeLimit(l){
  if (!l || typeof l !== 'object') return { type:'messages', value:10 };
  const type = ['messages','time'].includes(l.type)? l.type : 'messages';
  const value = Math.max(1, parseInt(l.value||10,10));
  return { type, value };
}
function sanitizeLayout(l){
  if (!l || typeof l !== 'object') return { orientation:'horizontal', leftPane:'stimuli', rightPane:'conversation' };
  const orientation = ['horizontal','vertical'].includes(l.orientation)? l.orientation : 'horizontal';
  const leftPane = ['stimuli','conversation'].includes(l.leftPane)? l.leftPane : 'stimuli';
  const rightPane = leftPane === 'stimuli' ? 'conversation' : 'stimuli';
  return { orientation, leftPane, rightPane };
}
function sanitizeInterpreter(i){
  if (!i || typeof i !== 'object') return { strategy:'auto', template:'' };
  const strategy = ['auto','strict'].includes(i.strategy)? i.strategy : 'auto';
  return { strategy, template:(i.template||'').toString() };
}
function sanitizeConversationRoot(c, optional = false){
  if (!c || typeof c !== 'object') {
    if (optional) return { engine:{ provider:'', model:'', temperature:0.7, top_p:1 }, agents:[], limit:{ type:'messages', value:10 }, task_description:'', expected_output:'' };
    return { trials:[ { id: crypto.randomUUID(), engine:{ provider:'', model:'', temperature:0.7, top_p:1 }, agents:[], limit:{ type:'messages', value:10 }, task_description:'', expected_output:'' } ] };
  }
  if (Array.isArray(c.trials)) {
    return { trials: c.trials.map((t,i)=> ({
      id: (typeof t.id==='string'&&t.id.trim())? t.id.trim(): crypto.randomUUID(),
      engine: sanitizeEngine(t.engine),
      // Accept either raw string IDs or objects with an id field
      agents: Array.isArray(t.agents)? t.agents.map(a => (typeof a === 'string' ? a : (a && typeof a.id === 'string' ? a.id : null))).filter(Boolean):[],
      limit: sanitizeLimit(t.limit),
      task_description: (t?.task_description||'').toString(),
      expected_output: (t?.expected_output||'').toString()
    })) };
  }
  // per stimulus conversation object variant
  return { 
    engine: sanitizeEngine(c.engine),
    agents: Array.isArray(c.agents)? c.agents.map(a => (typeof a === 'string' ? a : (a && typeof a.id === 'string' ? a.id : null))).filter(Boolean):[],
    limit: sanitizeLimit(c.limit),
    task_description: (c?.task_description||'').toString(),
    expected_output: (c?.expected_output||'').toString()
  };
}
